import qs from 'qs';
import axios from 'axios';
import UnauthenticatedError from '../errors/UnauthenticatedError';

/*
 * このファイルの関数群はAPIとの通信に利用される
 */

// APIのベースURL
const apiUrl = '';

// REVIEW: LaravelのCSRF対策のためにGET以外のリクエストでは一度/sanctum/csrf-cookieへGETリクエストを送る必要がある
(async () => {
  await axios('/sanctum/csrf-cookie');
})();

/**
 * APIにGETリクエストを送信
 *
 * @param url
 * @param data
 * @param additionalHeaders
 * @param options
 * @returns {Promise<Response>}
 */
export async function get(url, data = null, additionalHeaders = {}, options = {}) {
  const urlWithQuery = url + (data ? '?' + toQueryString(data) : '');
  return await request('GET', urlWithQuery, {}, additionalHeaders, options);
}

/**
 * APIにPOSTリクエストを送信
 *
 * @param url
 * @param data
 * @param additionalHeaders
 * @param options
 * @returns {Promise<Response>}
 */
export async function post(url, data = {}, additionalHeaders = {}, options = {}) {
  return await request('POST', url, data, additionalHeaders, options);
}

/**
 * APIにPUTリクエストを送信
 *
 * @param url
 * @param data
 * @param additionalHeaders
 * @param options
 * @returns {Promise<Response>}
 */
export async function put(url, data, additionalHeaders = {}, options = {}) {
  return await request('PUT', url, data, additionalHeaders, options);
}

/**
 * PATCH request API
 *
 * @param url
 * @param data
 * @param additionalHeaders
 * @param options
 * @returns {Promise<Response>}
 */
export async function patch(url, data = {}, additionalHeaders = {}, options = {}) {
  return await request('PATCH', url, data, additionalHeaders, options);
}

/**
 * APIにDELETEリクエストを送信
 *
 * @param url
 * @param data
 * @param additionalHeaders
 * @param options
 * @returns {Promise<Response>}
 */
export async function del(url, data, additionalHeaders = {}, options = {}) {
  return await request('DELETE', url, data, additionalHeaders, options);
}

/**
 * APIにファイルをダウンロードするリクエストを送信
 *
 * @param url
 * @param filename
 * @param method
 * @param data
 * @param additionalHeaders
 * @param options
 * @returns {Promise<Blob>}
 */
export async function download(url, filename, method = 'GET', data, additionalHeaders = {}, options = {}) {
  const config = {
    url: getUrl(url),
    method: method,
    responseType: 'blob',
    headers: additionalHeaders,
    ...options
  };

  if (method !== 'GET') {
    if (data instanceof FormData) {
      config['data'] = data;
    } else {
      config['data'] = JSON.stringify(data);
    }
  } else {
    config['url'] = getUrl(url + (data ? '?' + toQueryString(data) : ''));
  }

  return await axios(config)
    .then(res => {
      var a = document.createElement('a');
      a.href = window.URL.createObjectURL(res.data);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    })
    .catch(err => {
      throw err;
    });
}

/**
 * HTTPリクエストを送信
 *
 * @param method
 * @param url
 * @param data
 * @param additionalHeaders
 * @param options
 * @returns {Promise<Response>}
 */
async function request(method, url, data, additionalHeaders, options) {
  // Get current locale from localStorage
  const currentLocale = localStorage.getItem('locale') || 'ja';

  const headers = {
    'Accept': 'application/json',
    'Accept-Language': currentLocale,
    ...additionalHeaders
  };

  // Only set Content-Type for non-FormData requests
  if (!(data instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const config = {
    method: method,
    url: getUrl(url),
    headers: headers,
    ...options
  };

  if (method !== 'GET') {
    if (data instanceof FormData) {
      config['data'] = data;
    } else {
      config['data'] = JSON.stringify(data);
    }
  }

  return await axios(config)
    .then(handleResponse)
    .catch(handleError);
}

/**
 * パスのみのURLに対してAPIのURLを付与する。
 *
 * @param url
 * @returns {*}
 */
function getUrl(url) {
  if (url.indexOf('http') === 0) {
    return url;
  } else {
    return apiUrl + url;
  }
}

/**
 * レスポンスを処理
 *
 * @param res
 * @returns {Promise<*>}
 */
async function handleResponse(res) {
  return res.data;
}

/**
 * エラーを処理
 *
 * @param err
 * @returns {Promise<void>}
 */
async function handleError(err) {
  const res = err.response;

  if (res) {
    // 認証エラーの場合は各ログインページにリダイレクト
    if (res.status === 401) {
      throw new UnauthenticatedError();
    }

    // それ以外のエラー
    else {
      const data = res.data;
      let error;

      // アプリケーション由来のエラー
      if (data && data.message) {
        error = data;
      }

      // それ以外のエラー（ネットワークエラー等）
      else {
        error = {
          message: res.statusText
        }
      }

      error.status = res.status;

      throw error;
    }
  } else {
    throw err;
  }
}

/**
 *　オブジェクトをクエリ文字列に変換
 *
 * @param data
 * @returns {string|*}
 */
function toQueryString(data) {
  if (typeof data.toQueryString === 'function') {
    return data.toQueryString();
  } else {
    return qs.stringify(data, { arrayFormat: 'indices' });
  }
}
